/**
 * App Layout Component
 * Handles main layout structure and routing
 */

import React from "react";
import Sidebar from "../sidebar";
import DashboardLayout from "./DashboardLayout";
import EditorLayout from "./EditorLayout";
import MobileDesktopOnlyGate from "./MobileDesktopOnlyGate";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useClerkReady } from "../../hooks/app/useClerkReady";
import {
  MOBILE_DESKTOP_GATE_ENABLED,
} from "../../config/app/mobileExperience";
import { useIsMobileViewport } from "../../hooks/app/useIsMobileViewport";

import Dashboard from "../../features/dashboard/DashboardScreen";
import Tools from "../../features/dashboard/ToolsScreen";
import ToolPrepWorkspace from "../../pages/tools/ToolPrepWorkspace";
import Login from "../../pages/auth/Landing";
import StayTuned from "../../pages/maintenance/StayTuned";
import Editor from "../../pages/editor/Editor";

/* Batch pages */
import Batch from "../../pages/batch/batch";          // Upload screen
import BatchEditor from "../../pages/batch/BatchEditor"; // Editor screen

import BulkBgRemover from "../../pages/bulkbgremover/BulkBgRemover";
import ManageAssets from "../../pages/manage/assets/ManageAssets";
import Activity from "../../pages/manage/activity/Activity";
import Uploads from "../../pages/manage/uploads/uploads";
import BrandKit from "../../pages/manage/brand/brand-kit";
import InviteEarn from "../../pages/invite/InviteEarn";

import ProtectedRoute from "../routes/ProtectedRoute";
import PublicRoute from "../routes/PublicRoute";
import AuthSyncHandler from "../auth/AuthSyncHandler";
import ClaimCredits from "../popups/claim/ClaimCredits";
import { useMockWelcomeCreditsPopup } from "../../hooks/app/useMockWelcomeCreditsPopup";
import { useWelcomeCreditsPopup } from "../../hooks/app/useWelcomeCreditsPopup";
import { DashboardToolEntryGateProvider } from "../../contexts/DashboardToolEntryGate";

/** Keep ?ref= (and other query) when bouncing / → /login or /dashboard. */
function RootRedirect({ isSignedIn }) {
  const { search } = useLocation();
  return (
    <Navigate
      to={isSignedIn ? `/dashboard${search}` : `/login${search}`}
      replace
    />
  );
}

function LoginRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/login${search}`} replace />;
}

function AppLayout() {
  const { isSignedIn, clerkReady } = useClerkReady();
  const isMobileViewport = useIsMobileViewport();
  const {
    enabled: mockWelcomeEnabled,
    show: showMockWelcome,
    close: closeMockWelcome,
    blocksToolEntry: mockBlocksToolEntry,
  } = useMockWelcomeCreditsPopup();
  const {
    show: showWelcomeCredits,
    close: closeWelcomeCredits,
    blocksToolEntry: welcomeBlocksToolEntry,
  } = useWelcomeCreditsPopup({ enabled: !mockWelcomeEnabled });

  const showWelcomePopup = mockWelcomeEnabled ? showMockWelcome : showWelcomeCredits;
  const closeWelcomePopup = mockWelcomeEnabled ? closeMockWelcome : closeWelcomeCredits;
  const holdToolEntry = mockWelcomeEnabled ? mockBlocksToolEntry : welcomeBlocksToolEntry;

  /* Desktop-first: block all routes on small viewports until mobile UI ships */
  if (MOBILE_DESKTOP_GATE_ENABLED && isMobileViewport) {
    return <MobileDesktopOnlyGate />;
  }

  /* Full-screen loader only before Clerk's first ready frame. */
  if (!clerkReady) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "black",
          color: "white",
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <DashboardToolEntryGateProvider hold={holdToolEntry}>
    <div className="app-container">
      <AuthSyncHandler />

      {showWelcomePopup && (
        <ClaimCredits
          isOutOfCredits={false}
          onClose={closeWelcomePopup}
          onClaimed={closeWelcomePopup}
          skipMarkWelcomed={mockWelcomeEnabled}
        />
      )}

      <Routes>
        {/* Preview the hold screen without taking the rest of the app down */}
        <Route path="/stay-tuned" element={<StayTuned />} />

        {/* Login (Clerk sign-in / sign-up) */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />

        {/* Legacy landing URL */}
        <Route path="/landing" element={<LoginRedirect />} />

        {/* Root redirect — preserves ?ref= for local invite testing */}
        <Route
          path="/"
          element={<RootRedirect isSignedIn={isSignedIn} />}
        />

        {/* Dashboard */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Sidebar />
                <Dashboard />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Tools catalog */}
        <Route
          path="/tools"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Sidebar />
                <Tools />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Single-tool workspace (Dashboard/Tools entry — no Editor canvas) */}
        <Route
          path="/tools/:toolKey"
          element={
            <ProtectedRoute>
              <EditorLayout>
                <ToolPrepWorkspace />
              </EditorLayout>
            </ProtectedRoute>
          }
        />

        {/* Batch Upload Screen */}
        <Route
          path="/batch"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Sidebar />
                <Batch />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Batch Editor */}
        <Route
          path="/batch-editor"
          element={
            <ProtectedRoute>
              <EditorLayout>
                <BatchEditor />
              </EditorLayout>
            </ProtectedRoute>
          }
        />

        {/* Image Editor */}
        <Route
          path="/editor"
          element={
            <ProtectedRoute>
              <EditorLayout>
                <Editor />
              </EditorLayout>
            </ProtectedRoute>
          }
        />

        {/* Bulk Background Remover */}
        <Route
          path="/bulk-bg-remover"
          element={
            <ProtectedRoute>
              <EditorLayout>
                <BulkBgRemover />
              </EditorLayout>
            </ProtectedRoute>
          }
        />

        {/* Manage Assets */}
        <Route
          path="/my-designs"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Sidebar />
                <ManageAssets />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Activity */}
        <Route
          path="/activity"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Sidebar />
                <Activity />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Uploads */}
        <Route
          path="/uploads"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Sidebar />
                <Uploads />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Brand Kit */}
        <Route
          path="/brand-kit"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Sidebar />
                <BrandKit />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Invite & Earn */}
        <Route
          path="/invite"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Sidebar />
                <InviteEarn />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
      </Routes>

    </div>
    </DashboardToolEntryGateProvider>
  );
}

export default AppLayout;